import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Layers, MapPin, Lock,
  FileSearch, Workflow, AlertTriangle, CheckCircle2,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 25.16ae — /why-dara public reframe page.
//
// Strategic intent: pre-empt the "we already use Claude" objection
// (and its variants — "we already have an LLM bolt-on", "our engineering
// team is building this internally", "this is just a Claude wrapper").
// The page draws a hard line between the COMMODITY (the LLM) and
// the MOAT (everything DARA does above it). Public, indexable, written
// in the voice of someone who's been asked this exact question across
// every wagering and industrial pitch this quarter.
//
// Pattern matches /wagering visually (Bloomberg terminal). Not bespoke,
// not noindex — this is the page Manuel sends BEFORE the call when the
// prospect's first reaction to DARA is "we use Claude already."

// The 7 capability layers that exist ABOVE the LLM.
// Order matters: starts at the layer most easily confused with "a Claude
// prompt" and ladders up to the layer that is genuinely irreproducible.
const MOAT_LAYERS = [
  {
    Icon: Workflow,
    n: "01",
    label: "Multi-source ingest + normalisation",
    body: "DARA reads from AML engines, KYC platforms, integrity feeds, treasury systems, POS layers, BMS frameworks, industrial historians. Read-only, no rip-and-replace. The LLM doesn't normalise an OPC UA stream into a regulator-aligned event log — DARA does, before the LLM ever sees the data.",
    proves: "A Claude prompt can't connect to industrial historians, BMS frameworks, AML/KYC engines, or integrity-data feeds. The plumbing is the product.",
  },
  {
    Icon: MapPin,
    n: "02",
    label: "Jurisdiction-aware routing",
    body: "Every signal, report, and audit-log entry is routed to the correct regulator stack. AUSTRAC + NCPF + ACMA for AU; UKGC + AMLD6 + FCA for UK; FinCEN + state DGEs for US; MGA + AMLD6 for EU. An operator carrying 4 licences gets ONE compliance brain, not 4 regional teams running disconnected tooling.",
    proves: "An LLM doesn't know that AUSTRAC's 24-hour SMR clock is different from FinCEN's 30-day SAR clock. DARA does, because the rulebooks are config, not prompt.",
  },
  {
    Icon: FileSearch,
    n: "03",
    label: "Regulator-grade audit trail",
    body: "Every read, every output, every reasoning step is audit-logged at the database layer. Defensible under AUSTRAC, NCPF, UKGC, FinCEN, MGA review without a fire drill. Not a transcript log — a signed, schema-versioned, regulator-aligned evidence chain.",
    proves: "A Claude response is opaque to a regulator. DARA's audit trail is structured evidence that an examiner can subpoena, replay, and cross-reference.",
  },
  {
    Icon: Lock,
    n: "04",
    label: "Per-operator tenant isolation",
    body: "Per-operator database. Per-operator AES-256 encryption key. Zero cross-tenant queries. Every read audit-logged. Engineered at the architecture layer, not promised in a contract.",
    proves: "If two operators ran the same Claude prompt, neither can prove their customer graph wasn't visible to the other. With DARA, the tenant boundary is provable by inspecting the database, not by reading the SLA.",
  },
  {
    Icon: Layers,
    n: "05",
    label: "Four-pillar synthesis · one daily brief",
    body: "DARA produces ONE morning brief covering AML/CTF · Responsible Gambling · Integrity · Advertising — across every licence the operator holds. The Head of Compliance gets a 5-minute read before standup instead of stitching 5 separate dashboards built by 5 different vendors.",
    proves: "Anyone can prompt an LLM with one signal. Nobody is synthesising 4 regulator pillars × N jurisdictions × M point-tool outputs into one operationally useful brief in 5 minutes. That's a year of product work, not a prompt.",
  },
  {
    Icon: ShieldCheck,
    n: "06",
    label: "Eleven jurisdictional rulebooks",
    body: "Not 11 lookup tables — 11 living rulebooks that route signals, structure reports, format evidence chains, and version themselves as regulators update. Adding a new licence to an existing operator is configuration, not a six-month engineering project.",
    proves: "By 2027 the underlying LLM will be commoditised — Claude, Gemini, OpenAI, Mistral, Anthropic will all be interchangeable. The rulebooks won't. They take years to assemble and they are the durable asset.",
  },
  {
    Icon: AlertTriangle,
    n: "07",
    label: "Operator-branded artifact generation",
    body: "Every brief, PDF, regulator report, and analyst email DARA produces renders in the operator's brand — not in DARA's. Switching off DARA means switching off months of operator-branded audit artifacts that may already be cited in a regulator file.",
    proves: "Switching costs aren't built by integration depth alone — they're built by the existence of branded, regulator-cited artifacts the operator now relies on. Every day DARA runs, the lock-in deepens.",
  },
];

const COMMODITY_VS_MOAT = [
  {
    they: "We already use Claude internally for compliance summaries.",
    response: "Good — so you've validated the demand. DARA isn't a Claude prompt. It's the seven layers above the prompt. Your engineering team built layer-01 (a chatbot). The other six take 12-18 months and require domain depth no engineering team carries by default. We've already shipped them.",
  },
  {
    they: "Our engineers can build this internally.",
    response: "They can. At ~A$200k/year per senior engineer × 2-3 engineers × 18 months = A$600k-900k of internal build cost, plus ongoing maintenance, plus opportunity cost (those engineers aren't building your product). DARA's Paid POC is A$45-60k for 6-8 weeks. The math doesn't favour build at your scale.",
  },
  {
    they: "Anyone can wrap Claude in a chatbot.",
    response: "Correct — and DARA isn't a chatbot. It's an orchestration layer with jurisdiction routing, regulator-grade audit trails, tenant isolation, and 11 jurisdictional rulebooks. The chatbot at the bottom is the part you can replicate in a weekend. The other 95% is the product.",
  },
  {
    they: "What happens when the LLM gets better / cheaper / replaced?",
    response: "DARA upgrades the LLM. Your operational continuity doesn't change. The rulebooks, audit trail, tenant isolation, and brand-aware artifact engine all remain. We swap the model behind the curtain. Operators who hardcoded Claude into their internal tooling have to re-architect.",
  },
];

export default function WhyDara() {
  usePageview();

  // Phase 25.16af — per-route title + meta description so Google
  // search results and browser tabs surface the page's actual value
  // prop, not the default index.html title.
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = "Why DARA · Not a Claude Wrapper · DARA OS";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    if (meta) {
      meta.setAttribute(
        "content",
        "DARA isn't a Claude wrapper. The LLM is commodity. The moat is the 7 layers above it: multi-source ingest, jurisdiction-aware routing, regulator-grade audit trails, per-operator tenant isolation, four-pillar synthesis, 11 jurisdictional rulebooks, and operator-branded artifact generation."
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="why-dara-page">
      {/* ── HERO ───────────────────────────────────────────────── */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">
            // WHY DARA · NOT A CLAUDE WRAPPER
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-5xl">
            The LLM is commodity.<br />
            <span className="text-[#FFB000]">Everything above it</span> is DARA.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 leading-relaxed max-w-3xl">
            Any team can wrap an LLM in a chatbot in a weekend. The hard part is everything <span className="text-[#FFB000] font-bold">above</span> the model — and that's what you're actually buying.
          </p>

          {/* Phase 25.16ag — Bloomberg-terminal counter strip.
              Three live-counting numbers anchor the strategic claim
              in scannable, quantified form. Each counts up on mount.
              Sits between the hero copy and the CTAs. */}
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 max-w-3xl"
            data-testid="why-dara-counter-strip"
          >
            <CounterTile target={7} label="LAYERS ABOVE THE MODEL" sub="ingest · routing · audit · isolation · synthesis · rulebooks · artifacts" />
            <CounterTile target={11} label="JURISDICTIONAL RULEBOOKS" sub="AU · UK · EU · US · MGA · per-state · versioned" />
            <CounterTile target={1} label="DAILY BRIEF" sub="all 4 regulator pillars · every licence · 5-min read" highlight />
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/wagering"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity"
              data-testid="why-dara-cta-wagering"
            >
              See it on /wagering <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/industrial-demo"
              className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:border-[#FFB000] hover:text-[#FFB000] transition-colors"
              data-testid="why-dara-cta-industrial"
            >
              See it on /industrial-demo <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── THE SEVEN LAYERS ABOVE THE LLM ─────────────────────── */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE STACK · SEVEN LAYERS ABOVE THE MODEL
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            Where the moat actually lives.
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            Each layer is independently irreproducible by a Claude prompt. Together they're the product. Read top-to-bottom — the layers get harder to replicate as you descend.
          </p>

          <div className="mt-10 space-y-4">
            {MOAT_LAYERS.map((L) => (
              <MoatLayerRow key={L.n} L={L} />
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMODITY-VS-MOAT FAQ ──────────────────────────────── */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE FOUR OBJECTIONS · ANSWERED
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            What we hear, and what's actually true.
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl">
            Every operator considering DARA arrives with one of these four objections. The honest answer to each.
          </p>

          <div className="mt-10 space-y-6">
            {COMMODITY_VS_MOAT.map((q, i) => (
              <ObjectionRow key={i} n={i + 1} they={q.they} response={q.response} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CLOSING REFRAME ────────────────────────────────────── */}
      <section className="border-b border-zinc-900 bg-zinc-950">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE REFRAME
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
            Stripe doesn't sell AWS. Notion doesn't sell OpenAI. <span className="text-[#FFB000]">DARA doesn't sell Claude.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 leading-relaxed">
            They each sell the product layer their customers actually use — the workflow, the orchestration, the structured outcome — and they each disclose their infrastructure subprocessors in their privacy and security pages because that's what mature B2B SaaS does. DARA's the same shape. The LLM is one of our ingredients. What you're buying is the meal.
          </p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <StatCard label="The commodity layer" value="LLM" sub="By 2027: fully interchangeable" />
            <StatCard label="The product layer" value="DARA" sub="7 layers · 12-18 months of build · domain depth" />
            <StatCard label="The durable asset" value="Rulebooks" sub="11 jurisdictions · versioned · provable" highlight />
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/wagering?source=why-dara"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity"
              data-testid="why-dara-closing-cta-wagering"
            >
              DARA Wagering <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-200 font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:border-[#FFB000] hover:text-[#FFB000] transition-colors"
              data-testid="why-dara-closing-cta-home"
            >
              ← Back to DARA OS
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA STRIP · ready to talk? ─────────────────────────── */}
      {/* Phase 25.16ah — closes the inbound signal-capture loop.
          Prior to this strip, a prospect reading /why-dara had to
          navigate elsewhere to find a contact form. Now they get a
          single, terminal-aesthetic CTA that drops them straight into
          the wagering lead form with source=why-dara attribution. */}
      <section className="border-b border-zinc-900 bg-black" data-testid="why-dara-cta-strip">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="border border-[#FFB000]/40 bg-[#FFB000]/5 p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6 lg:gap-8 items-center">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">
                // READY TO TALK?
              </div>
              <h3 className="font-heading font-black text-xl sm:text-2xl tracking-tight text-white leading-tight">
                Twenty minutes. Founder-led. <span className="text-[#FFB000]">No vendor theatre.</span>
              </h3>
              <p className="mt-2 text-sm text-zinc-300 leading-relaxed max-w-2xl">
                If the seven layers landed for you, the next conversation is whether DARA fits your operator stack. Operator-to-operator. Honest read on whether we're a 6-week paid POC, a 12-week Lighthouse, or not the right fit right now.
              </p>
            </div>
            <Link
              to="/wagering?source=why-dara#engage"
              data-testid="why-dara-final-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-mono text-[11px] sm:text-xs uppercase tracking-widest px-6 py-4 hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Book the 20-min call <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function MoatLayerRow({ L }) {
  const { Icon, n, label, body, proves } = L;
  return (
    <div
      className="border border-zinc-900 p-5 sm:p-6 hover:border-[#FFB000]/40 transition-colors"
      data-testid={`why-dara-layer-${n}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="font-mono text-[11px] sm:text-[10px] text-[#FFB000] mb-1">// {n}</div>
          <Icon className="w-5 h-5 text-[#FFB000]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-black text-lg sm:text-xl tracking-tight">{label}</h3>
          <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{body}</p>
          <div className="mt-3 border-l-2 border-[#FFB000]/40 pl-3">
            <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">
              // WHY IT&apos;S NOT A PROMPT
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">{proves}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ObjectionRow({ n, they, response }) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 border-l-2 border-zinc-800 pl-4 sm:pl-6 py-2"
      data-testid={`why-dara-objection-${n}`}
    >
      <div>
        <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-zinc-500 mb-1">
          // 0{n} · WHAT WE HEAR
        </div>
        <p className="text-sm text-zinc-400 italic leading-relaxed">&ldquo;{they}&rdquo;</p>
      </div>
      <div>
        <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] mb-1 flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3" /> THE ANSWER
        </div>
        <p className="text-sm text-zinc-200 leading-relaxed">{response}</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, highlight }) {
  return (
    <div
      className={`border p-4 ${
        highlight
          ? "border-[#FFB000]/40 bg-[#FFB000]/5"
          : "border-zinc-900 bg-black"
      }`}
    >
      <div className="font-mono text-[10px] sm:text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-heading font-black text-2xl mt-1 ${highlight ? "text-[#FFB000]" : "text-white"}`}>
        {value}
      </div>
      <div className="font-mono text-[11px] sm:text-[10px] text-zinc-400 mt-1.5 leading-relaxed">{sub}</div>
    </div>
  );
}


// Bloomberg-terminal style number counter. Counts from 0 → target
// over ~1.4s with easing, on mount. Pure CSS-free animation, no
// dependencies. Looks alive without being distracting.
function CounterTile({ target, label, sub, highlight }) {
  const [value, setValue] = React.useState(0);
  React.useEffect(() => {
    const duration = 1400; // ms
    const startedAt = performance.now();
    let raf;
    const tick = (now) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / duration);
      // ease-out-cubic — fast start, soft landing
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  return (
    <div
      className={`p-4 sm:p-5 ${highlight ? "bg-[#FFB000]/5" : "bg-black"}`}
      data-testid={`why-dara-counter-${target}`}
    >
      <div className="flex items-baseline gap-1.5">
        <div
          className={`font-heading font-black text-4xl sm:text-5xl tracking-tight tabular-nums ${
            highlight ? "text-[#FFB000]" : "text-white"
          }`}
        >
          {value}
        </div>
        {value === target && (
          <span className="w-1.5 h-1.5 rounded-full bg-[#00E676] animate-pulse" />
        )}
      </div>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mt-2">
        // {label}
      </div>
      <div className="font-mono text-[11px] sm:text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
        {sub}
      </div>
    </div>
  );
}
